import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuthStore, rolesAtLeast } from "@/stores/auth";
import { apiFetch } from "@/api/client";

export default function AuthLayout() {
  const user = useAuthStore((s) => s.user)!;
  const refreshToken = useAuthStore((s) => s.refreshToken);
  const clear = useAuthStore((s) => s.clear);
  const navigate = useNavigate();

  const isOrgAdmin = rolesAtLeast(user.role, "ORG_ADMIN");
  const isSuper = user.role === "SUPER_ADMIN";

  async function onLogout() {
    try {
      const accessToken = useAuthStore.getState().accessToken;
      await apiFetch("/auth/logout", {
        method: "POST",
        headers: accessToken ? { Authorization: `Bearer ${accessToken}` } : {},
        body: refreshToken ? JSON.stringify({ refreshToken }) : undefined,
      });
    } catch {
      // ignore
    } finally {
      clear();
      navigate("/login", { replace: true });
    }
  }

  return (
    <div className="min-h-screen flex flex-col bg-muted">
      <header className="bg-background border-b border-border">
        <div className="max-w-6xl mx-auto px-6 h-14 flex items-center gap-6">
          <Link to="/" className="font-semibold text-sm">Meta Ads</Link>
          <nav className="flex items-center gap-1 text-sm">
            <NavTab to="/" exact>看板</NavTab>
            <NavTab to="/ad-accounts">广告账户</NavTab>
            {isOrgAdmin && <NavTab to="/invitations">邀请</NavTab>}
            {isSuper && <NavTab to="/organizations">公司</NavTab>}
          </nav>
          <div className="ml-auto flex items-center gap-3 text-sm">
            <RoleBadge role={user.role} />
            <span className="text-muted-foreground">{user.email}</span>
            <button
              onClick={onLogout}
              className="rounded-md px-3 py-1.5 text-sm border border-border hover:bg-muted"
            >
              退出
            </button>
          </div>
        </div>
      </header>
      <main className="flex-1">
        <div className="max-w-6xl mx-auto px-6 py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}

function NavTab({ to, exact, children }: { to: string; exact?: boolean; children: React.ReactNode }) {
  return (
    <NavLink
      to={to}
      end={exact}
      className={({ isActive }) =>
        `px-3 py-1.5 rounded-md transition-colors ${
          isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground hover:bg-muted"
        }`
      }
    >
      {children}
    </NavLink>
  );
}

function RoleBadge({ role }: { role: string }) {
  const cls =
    role === "SUPER_ADMIN"
      ? "bg-violet-100 text-violet-800"
      : role === "ORG_ADMIN"
      ? "bg-sky-100 text-sky-800"
      : "bg-emerald-100 text-emerald-800";
  return <span className={`text-xs px-2 py-0.5 rounded ${cls}`}>{role}</span>;
}
